import type { ReactNode } from 'react'
import { Table } from '@heroui/react'

import {
  branchLabel,
  chipStyle,
  money,
  sessionStatusStyles,
  StatusChip,
  varianceLabel,
} from '@/features/pos'
import type { PosSession } from '@/features/pos'

interface CloseOutsTableProps {
  sessions: PosSession[]
  /** Opens the drawer's X and Z. */
  onOpen: (session: PosSession) => void
  /** Under the body, inside the table's frame — a `ReportPager`. */
  footer?: ReactNode
}

/**
 * One row per close-out: what a drawer sold, what it should have held, and what
 * was in it.
 *
 * The variance is the column this table exists for, so it is the only one that
 * takes colour. Three states, not two: a drawer out by more than a rounding
 * difference is amber, one that balanced is plain, and one with NO variance at
 * all — still open, or force-closed without a count — shows a dash. That third
 * case is the one a zero would lie about, by claiming a reconciliation nobody
 * performed.
 *
 * Net comes off the frozen Z, so it is blank on exactly those same rows. Better
 * an honest gap than a figure re-derived today for a period that was signed off
 * in August.
 */
export function CloseOutsTable({ sessions, onOpen, footer }: CloseOutsTableProps) {
  const totals = sessions.reduce(
    (sum, session) => ({
      net: sum.net + (session.netSales ?? 0),
      expected: sum.expected + (session.expectedCash ?? 0),
      counted: sum.counted + (session.countedCash ?? 0),
      variance: sum.variance + (session.variance ?? 0),
    }),
    { net: 0, expected: 0, counted: 0, variance: 0 },
  )

  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content
          aria-label="Close-outs"
          selectionMode="single"
          selectionBehavior="replace"
          onRowAction={(key) => {
            const session = sessions.find((row) => row.id === String(key))

            if (session) onOpen(session)
          }}
        >
          <Table.Header>
            <Table.Column id="date" isRowHeader>
              Business date
            </Table.Column>
            <Table.Column id="register">Register</Table.Column>
            <Table.Column id="branch">Branch</Table.Column>
            <Table.Column id="status">Status</Table.Column>
            <Table.Column id="net">Net</Table.Column>
            <Table.Column id="expected">Expected cash</Table.Column>
            <Table.Column id="counted">Counted</Table.Column>
            <Table.Column id="variance">Variance</Table.Column>
          </Table.Header>

          <Table.Body>
            {sessions.map((session) => (
              <Table.Row key={session.id} id={session.id}>
                <Table.Cell>
                  <span className="tabular-nums">{businessDate(session.openedAt)}</span>
                </Table.Cell>

                <Table.Cell>
                  <span className="tabular-nums">{session.registerCode ?? '—'}</span>
                </Table.Cell>

                <Table.Cell>
                  <span className="text-accent">{branchLabel(session.branch)}</span>
                </Table.Cell>

                <Table.Cell>
                  <StatusChip style={chipStyle(sessionStatusStyles, session.status)} />
                </Table.Cell>

                <Table.Cell>
                  <span className={`tabular-nums ${session.netSales === null ? 'text-muted' : ''}`}>
                    {session.netSales === null ? '—' : money(session.netSales)}
                  </span>
                </Table.Cell>

                <Table.Cell>
                  <span className="text-muted tabular-nums">{money(session.expectedCash)}</span>
                </Table.Cell>

                <Table.Cell>
                  <span className="tabular-nums">{money(session.countedCash)}</span>
                </Table.Cell>

                <Table.Cell>
                  <span
                    className={`tabular-nums ${
                      session.varianceNeedsAttention
                        ? 'font-medium text-amber-700 dark:text-amber-400'
                        : session.variance === null
                          ? 'text-muted'
                          : ''
                    }`}
                  >
                    {varianceLabel(session.variance)}
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}

            {/* The totals are of the ROWS ON SCREEN, which is why the meta line
                above says how many of how many those are. A footer that summed a
                page while reading like a period total is the oldest way to sign
                off the wrong number. */}
            <Table.Row id="total">
              <Table.Cell>
                <span className="font-medium">
                  {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
                </span>
              </Table.Cell>
              <Table.Cell>{''}</Table.Cell>
              <Table.Cell>{''}</Table.Cell>
              <Table.Cell>{''}</Table.Cell>
              <Table.Cell>
                <span className="font-medium tabular-nums">{money(totals.net)}</span>
              </Table.Cell>
              <Table.Cell>
                <span className="text-muted font-medium tabular-nums">
                  {money(totals.expected)}
                </span>
              </Table.Cell>
              <Table.Cell>
                <span className="font-medium tabular-nums">{money(totals.counted)}</span>
              </Table.Cell>
              <Table.Cell>
                <span className="font-medium tabular-nums">{varianceLabel(totals.variance)}</span>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>

      {footer && <Table.Footer>{footer}</Table.Footer>}
    </Table>
  )
}

/**
 * The night a drawer belongs to — the day it OPENED on.
 *
 * A session that opened at 22:00 and closed at 02:00 is filed under the night it
 * began, which is the night a manager looks for it under and the same rule the
 * API sorts by. Keying on `closedAt` would file half a Saturday night under
 * Sunday.
 */
function businessDate(openedAt: string): string {
  const at = new Date(openedAt)

  if (Number.isNaN(at.getTime())) return '—'

  const month = String(at.getMonth() + 1).padStart(2, '0')
  const day = String(at.getDate()).padStart(2, '0')

  return `${at.getFullYear()}-${month}-${day}`
}
